import type { AnyLineTax, LineTax } from "components/SalesOrderDetails/types";

export const f2 = (v: any) => Math.round((Number(v) || 0) * 100) / 100;
export const f4 = (v: any) => Math.round((Number(v) || 0) * 10000) / 10000;

export function coerceToLineTax(
  entry: AnyLineTax | undefined,
  qty: number,
  unit: number,
  appliedState: string
): LineTax {
  const total = Number(entry?.total_product_tax_amount ?? entry?.amount ?? 0);
  const pct = Number(entry?.tax_percentage ?? ((entry?.rate ?? 0) * 100));
  const unitTax = qty > 0 ? total / qty : 0;

  return {
    tax_percentage: f2(pct),
    unit_product_tax_amount: f2(unitTax),
    total_product_tax_amount: f2(total),
    taxable: Boolean(entry?.taxable ?? (pct > 0 || total > 0)),
    tax_details: {
      combined_rate: f2(entry?.tax_details?.combined_rate ?? pct),
      tax_amount: f2(entry?.tax_details?.tax_amount ?? total),
      applied_state: entry?.tax_details?.applied_state ?? appliedState,
    },
  };
}

export const getItemTinfo = (item: any, liveMap: Record<string, { amount: number; rate: number }>) => {
  const liveKey = String(item?.sku || item?.itemId || item?.product_id || "");
  const live = liveMap?.[liveKey];
  if (live) return { amount: Number(live.amount || 0), rate: Number(live.rate || 0) };

  const iti = item?.item_tax_info;
  if (iti) {
    return {
      amount: Number(iti.total_product_tax_amount || 0),
      rate: Number(iti.tax_percentage || 0) / 100,
    };
  }
  return { amount: 0, rate: 0 };
};

export const getMarkerColors = (text: string, idx: number, palette: string[]) => {
  const t = (text || "").toLowerCase();

  if (t.includes("delivered")) {
    return { dot: "#00c951", halo: "rgba(34,197,94,.22)", bubble: "#EAF7F1" };
  }
  if (t.includes("shipped")) {
    return { dot: "#2b7fff", halo: "rgba(59,130,246,.22)", bubble: "#E8F1FF" };
  }
  if (t.includes("payment")) {
    return { dot: "#ff6900", halo: "rgba(245,158,11,.22)", bubble: "#FFF4E8" };
  }
  if (t.includes("pending") || t.includes("awaiting") || t.includes("net 30") || t.includes("net 40")) {
    return { dot: "#ff6900", halo: "rgba(245,158,11,.22)", bubble: "#FFF4E8" };
  }
  if (t.includes("confirm")) {
    return { dot: "#10B981", halo: "rgba(16,185,129,.22)", bubble: "#EAF7F1" };
  }
  if (t.includes("quote")) {
    return { dot: "#6366f1", halo: "rgba(99,102,241,.22)", bubble: "#EEF2FF" };
  }
  if (t.includes("invoice sent")) {
    return { dot: "#8b5cf6", halo: "rgba(139,92,246,.22)", bubble: "#F3E8FF" };
  }
  if (t.includes("cancel")) {
    return { dot: "#DC2626", halo: "rgba(220,38,38,.22)", bubble: "#FEE2E2" };
  }
  if (t.includes("created") || t.includes("placed")) {
    return { dot: "#6366F1", halo: "rgba(99,102,241,.22)", bubble: "#EEF2FF" };
  }
  const fallback = palette[idx % palette.length] || "#9CA3AF";
  return { dot: fallback, halo: "rgba(156,163,175,.22)", bubble: "#F3F4F6" };
};

export function renderShipping(order: any) {
  const method = String(order?.shipping_method?.service_name ?? order?.shipping?.method ?? "").trim();

  const amountRaw = order?.shipping_method?.cost ?? order?.shipping_amount ?? order?.shipping?.amount;

  const hasAmount = amountRaw !== undefined && amountRaw !== null && amountRaw !== "";
  const amount = Number(amountRaw);
  const hasMethod = method.length > 0;

  if (!hasMethod && !hasAmount) return "–";

  if (hasMethod && hasAmount && Number.isFinite(amount) && amount === 0) {
    return "Free Shipping";
  }

  if (!hasMethod) return "–";

  const price = Number.isFinite(amount) ? ` $${amount.toFixed(2)}` : "";
  return `${method}${price}`;
}

export type CustomInfoPair = { key: string; value: string };

/** Parse `custom_information` (e.g. "agentname:ramu,\nagentphone:123") into label/value rows. */
export function parseCustomInformation(info: string | undefined | null): CustomInfoPair[] {
  const raw = String(info ?? "").trim();
  if (!raw) return [];

  return raw
    .split(/\r?\n|,/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIdx = part.indexOf(":");
      if (colonIdx < 0) return { key: part, value: "" };
      return {
        key: part.slice(0, colonIdx).trim(),
        value: part.slice(colonIdx + 1).trim(),
      };
    })
    .filter(({ key, value }) => key.length > 0 || value.length > 0);
}

export function getOrderCartId(order: any): string | null {
  if (!order) return null;
  const candidates = [
    order?.cart_ids,
    order?.cart_id,
    order?.cartId,
    order?.order_tax_summary?.cart_ids,
    order?.order_tax_summary?.cart_id,
    order?.order_tax_summary?.cartId,
    order?.order_tax_summary?.taxcloud_cart_id,
    order?.order_tax_summary?.taxCloudCartId,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;
    const str = String(candidate).trim();
    if (str.length) return str;
  }

  return null;
}

/** Signage + taxcloud: block quote/payment/shipping until Get Live Taxes sets draft (or complete). */
export function salesOrderNeedsGetLiveTaxGate(
  activeTaxType: string,
  taxStatusRaw: unknown
): boolean {
  if (String(activeTaxType || "").trim().toLowerCase() !== "taxcloud") return false;
  const taxStatus = String(taxStatusRaw ?? "").trim().toLowerCase();
  return taxStatus !== "draft" && taxStatus !== "complete";
}
